/**
 * Composition root for 3Halves behavioural tracking.
 *
 * The only file in this folder with side effects, and the only one that reads a global. It wires
 * the four collaborators together, starts the tracker, and keeps it in step with OneTrust.
 *
 * Imported once from resources/scripts/app.js, so it runs on every front-end page. It costs an
 * unconfigured environment one property read, and an anonymous visitor no requests at all.
 */

import { parseConfig } from './config';
import { createConsentSource } from './consent';
import {
	analyticsCategories,
	createLoyaltyConsentGate,
	observeConsentAttribute,
	readConsentAttribute,
} from './loyaltyConsentGate';
import {
	createAuthReader,
	createTokenProvider,
	fanId,
	liveContract,
	onFan,
	onIdentityChange,
} from './urcAuth';
import { clearVisitorIdentity, createTracker } from './tracker';

/**
 * Published to `<html data-score-tracker>` so the state is legible without a debugger.
 *
 * `unconfigured` — no keys for this environment; nothing was built.
 * `active`       — collecting.
 * `denied`       — consent withheld for the configured group.
 * `denied-loyalty` — the OneTrust group allows it, but the fan has not granted loyalty consent.
 *                  Distinct because the two are fixed in completely different places, and telling
 *                  them apart from the outside is otherwise impossible.
 * `idle`         — consent granted but collection suppressed, i.e. Do Not Track.
 * `error`        — startup threw. The page is unaffected; nothing is collected.
 */
const STATE_ATTRIBUTE = 'scoreTracker';

/** Window handle for `inspect()`. Only published in debug environments. */
const DEBUG_HANDLE = '__scoreTracker';

/** @type {import('@3halves-labs/score-tracker').ScoreTracker|null} */
let instance = null;

/**
 * The live tracker, for callers that need the full API.
 *
 * @return {import('@3halves-labs/score-tracker').ScoreTracker|null} The instance, or null when
 *         tracking is not configured in this environment.
 */
export function getTracker() {
	return instance;
}

/**
 * Record a custom event.
 *
 * A no-op when tracking is unconfigured, so callers never need to guard. Events raised before
 * consent is granted are dropped rather than buffered — that is the SDK's contract, and the reason
 * this cannot be used to reconstruct a pre-consent session.
 *
 * @param {string}   name               Event name.
 * @param {Object}   [props]            Event properties. No personal data.
 * @param {Object}   [options]          Passed through to the SDK.
 * @param {string[]} [options.requires] Consent categories the event needs.
 * @return {void}
 */
export function trackEvent( name, props, options ) {
	if ( ! instance ) {
		return;
	}

	instance.track( name, props, options );
}

/**
 * @param {string} state One of the STATE_ATTRIBUTE values.
 * @return {void}
 */
function publishState( state ) {
	if ( typeof document !== 'undefined' && document.documentElement ) {
		document.documentElement.dataset[ STATE_ATTRIBUTE ] = state;
	}
}

/**
 * Derive the published state from a tracker snapshot.
 *
 * `started` is the truth about whether anything is being collected: Do Not Track suppresses
 * collection without touching the consent verdict, so the two questions have to be read separately.
 *
 * @param {import('@3halves-labs/score-tracker').InspectSnapshot} snapshot Tracker snapshot.
 * @param {Object|null}                                           loyalty  The loyalty gate, or null.
 * @return {string} A STATE_ATTRIBUTE value.
 */
function describeState( snapshot, loyalty ) {
	if ( snapshot.started ) {
		return 'active';
	}

	if ( snapshot.consent.granted ) {
		return 'idle';
	}

	// WHICH gate said no, because the two are fixed in completely different places -- one in Auth0,
	// the other in a cookie banner -- and `denied` for both sends whoever is debugging to the wrong
	// one.
	return loyalty ? 'denied-loyalty' : 'denied';
}

/**
 * Build everything and start.
 *
 * @return {void}
 */
function bootstrap() {
	const config = parseConfig( window.urcScoreTracker );

	if ( ! config ) {
		publishState( 'unconfigured' );

		return;
	}

	/*
	 * Exactly ONE of these gates collection, and they are constructed exclusively so that is
	 * structural rather than a rule buried in a predicate.
	 *
	 * **When the loyalty consent surface is on, the fan's Auth0 opt-in is the ONLY requirement and
	 * OneTrust is not consulted at all** -- not read, not subscribed to, not constructed. 3Halves are
	 * the loyalty vendor and a signed-in fan's events carry their loyalty JWT as `context.jwt`, so
	 * the client's position is that taking part in the programme is the basis for that flow.
	 *
	 * **Read this before assuming it is the safer of the two.** It is not a superset of the OneTrust
	 * gate: a fan who explicitly DECLINED analytics cookies and then joined the programme is now
	 * collected, where before they were not -- and the tracker keeps its state in `localStorage`.
	 * That holds up only for as long as the loyalty consent copy covers analytics and storage. It is
	 * a client decision, explicitly provisional, and it is recorded as one -- see D-071 and
	 * docs/subsystems/tracking.md.
	 *
	 * When the surface is off, OneTrust gates collection exactly as it did before any of this
	 * existed.
	 */
	const loyalty = config.requireLoyaltyConsent
		? createLoyaltyConsentGate( {
				read: readConsentAttribute,
				observe: observeConsentAttribute,
		  } )
		: null;

	const onetrust = loyalty ? null : createConsentSource( { group: config.consentGroup } );

	/**
	 * @return {Object} The SDK's consent categories, from whichever gate is in force.
	 */
	function readConsent() {
		return loyalty ? analyticsCategories( loyalty.read() ) : onetrust.read();
	}

	/*
	 * Identity needs no switch of its own, and it used to have one.
	 *
	 * `config.tokenCookie` was that switch: three values had to be published from PHP -- the cookie
	 * name, the audience and an expiry margin -- and identity was off unless all of them arrived,
	 * because a cookie with no audience to check it against would have forwarded whatever it found.
	 * None of that is published any more. `window.urcAuth` either exists or does not, and when it
	 * does `getAccessToken()` answers null wherever no loyalty audience is configured -- so an
	 * install with no Auth0 and an install with no loyalty API both degrade to anonymous batches
	 * with nothing to configure. That is the same outcome the switch produced, reached by asking
	 * the one thing that knows.
	 */
	const getToken = createTokenProvider( { reader: createAuthReader( liveContract ) } );

	/**
	 * The fan the CURRENT instance has been told about, or '' for none.
	 *
	 * Scoped to the instance rather than to the document, because `identify()` emits an event every
	 * time it is called and a rebuilt tracker has to be told again.
	 */
	let identifiedSub = '';

	/**
	 * Build an instance and publish it. Separate from `apply()` because an account switch needs a
	 * NEW one -- see `rebuild()`.
	 *
	 * @return {Object} The tracker.
	 */
	function build() {
		instance = createTracker( { config, getConsent: readConsent, getToken } );
		// A new instance has never been told who the fan is, whatever the old one knew.
		identifiedSub = '';

		if ( config.debug ) {
			// Deliberately debug-only. Acceptance testing needs a handle for `inspect()`;
			// production does not need a tracker on the window.
			window[ DEBUG_HANDLE ] = instance;
		}

		return instance;
	}

	build();

	// Serialised rather than fired in parallel. OneTrust can dispatch twice in quick succession,
	// and `init()` is async — overlapping a teardown with a startup would leave listeners bound to
	// an instance that has already been shut down.
	let queue = Promise.resolve();

	/**
	 * @param {boolean} restart Whether an instance is already running and must be torn down first.
	 * @return {void}
	 */
	function apply( restart ) {
		queue = queue
			.then( () => {
				if ( restart ) {
					// Flushes the buffer through sendBeacon, unbinds every listener, restores the
					// patched history methods and locks the gate. On a revocation this is what
					// stops collection; on a re-grant it is what makes the next init() clean.
					instance.shutdown();
				}

				return instance.init();
			} )
			.then( () => publishState( describeState( instance.inspect(), loyalty ) ) )
			.catch( error => {
				publishState( 'error' );
				// eslint-disable-next-line no-console
				console.warn( '[tracking] startup failed', error );
			} );
	}

	apply( false );

	/*
	 * A change of fan needs a NEW instance, and `shutdown()` then `init()` is not one.
	 *
	 * **The SDK assigns `anonymousId` and `sessionId` in its CONSTRUCTOR**, from `score.anonId`
	 * (localStorage) and `score.sessionId` (sessionStorage), and neither `init()` nor `shutdown()`
	 * reads, writes or clears either. There is no `reset()` on the class and `anonymousId` is
	 * private. So a restart leaves the previous fan's visitor id in place and the next envelope
	 * carries **A's `context.anonymousId` under B's `context.jwt`** -- which is a cross-fan link in
	 * a third party's warehouse, and the one thing the account-switch work is for.
	 *
	 * So: shut the old one down (which flushes its buffer through `sendBeacon`, so nothing already
	 * collected is lost), delete the keys, construct a new instance, and start it.
	 *
	 * **Two costs, both accepted, because neither is a cross-fan link.**
	 *
	 * - `autoPageviews` is on, so `init()` emits a pageview -- a second one for this document. On a
	 *   sign-in that is a page load anyway; on an in-document switch it is one extra event.
	 * - The anonymous→identified link is lost: a fresh `anonId` means the browsing this person did
	 *   before signing in cannot be joined to the session after. That linkage is exactly what the
	 *   loyalty opt-in gates in the first place (D-071), and it is not worth keeping at the price of
	 *   the one above it.
	 *
	 * **It fires on a genuine change only.** `onIdentityChange` compares the fan against the owner
	 * recorded in this tab's `sessionStorage`, so an ordinary load for the tab's own fan fires
	 * nothing -- see `resources/scripts/auth/store.js`. That property is what makes this affordable;
	 * before 2026-09-07 the store was built with no storage and this would have rebuilt the tracker
	 * on every page load, doubling every pageview.
	 */
	onIdentityChange( liveContract, () => {
		queue = queue
			.then( () => {
				instance.shutdown();
				clearVisitorIdentity();

				return build().init();
			} )
			.then( () => publishState( describeState( instance.inspect(), loyalty ) ) )
			.catch( error => {
				publishState( 'error' );
				// eslint-disable-next-line no-console
				console.warn( '[tracking] could not rebuild after an account switch', error );
			} );
	} );

	/*
	 * Tell the SDK who the fan is, so the browsing done before sign-in can be attributed to them.
	 *
	 * **What this actually buys, and it is not points.** `identify()` fires the SDK's
	 * `linkAnonymous()`, which claims this browser's `anonymousId` for the fan by POSTing it to
	 * `/api/identity/link-anonymous` under the fan's own bearer. The ingest can then join events on
	 * `context.anonymousId` through that link table -- which matters for the browsing done BEFORE
	 * sign-in, and for nothing after it: **an event carrying `context.jwt` is attributed on its own**,
	 * with no link row. That was measured by outlier-stats on 2026-09-21 (an unlinked `anonymousId`
	 * plus the JWT credited a fan) and again on 2026-09-30, and it corrects the vendor's description
	 * of the JWT as a fallback, which this comment used to repeat. This site emits **no custom events
	 * at all** -- `trackEvent()` has no caller -- so the gain here is analytics attribution. On the
	 * match centre the events that pay are the ones named in the tenant's rules: `quiz_completed`
	 * maps to no goal on 2026-09-30 (`GET /api/challenges/{15,65,66}/goal-mapping`) and pays
	 * nothing.
	 *
	 * **Why this rides `onFan` and not `onIdentityChange`.** The latter fires on a CHANGE of fan,
	 * which a fan arriving already signed in is not -- and on a multi-page site where every
	 * navigation is a full load, that is the ordinary case and the one with pre-sign-in browsing
	 * worth linking. `onFan` sees every settled state, so it covers both.
	 *
	 * **It cannot land on the wrong instance, and that is load-bearing rather than lucky.**
	 * `resources/scripts/auth/store.js` announces its identity listeners BEFORE its change
	 * listeners, synchronously and in that order on purpose. So on a switch the rebuild above is
	 * already in `queue` before this handler runs, and the `identify()` below is enqueued behind it
	 * -- it reaches the NEW instance, never the outgoing one. Were the order the other way round,
	 * this would stamp fan B onto fan A's `anonymousId` and then flush it through `shutdown()`,
	 * which is precisely the cross-fan link the rebuild exists to prevent.
	 *
	 * **The check and the record both happen inside the queue**, because `build()` resets
	 * `identifiedSub` and doing either outside would race with it.
	 *
	 * **No `replayAnonymousEvents`.** The link table is the attribution mechanism; replay is a
	 * second one that re-sends the buffer as `_backfill` duplicates. Omitting the option discards
	 * that buffer silently, which is the intended outcome here and not an oversight.
	 */
	onFan( liveContract, state => {
		const sub = fanId( state );

		if ( sub === '' ) {
			return;
		}

		queue = queue
			.then( () => {
				if ( sub === identifiedSub ) {
					return;
				}

				instance.identify( sub );
				identifiedSub = sub;
			} )
			.catch( error => {
				// Attribution is not worth a broken tracker: the link call is fire-and-forget
				// inside the SDK, so anything thrown here is from identify() itself.
				// eslint-disable-next-line no-console
				console.warn( '[tracking] could not identify the fan', error );
			} );
	} );

	/*
	 * One feed, from whichever gate is in force, and only that one.
	 *
	 * Both filter to genuine changes -- consent.js on the decision, loyaltyConsentGate.js on the
	 * boolean -- because without that guard every cookie-banner interaction, or every intermediate
	 * value of the consent attribute, would restart the tracker and re-emit the automatic pageview.
	 * Subscribing to OneTrust as well while the loyalty gate is in force would do exactly that, for
	 * a signal whose answer is no longer consulted.
	 *
	 * The loyalty feed is also what makes the ordering safe: that bundle resolves its answer
	 * asynchronously -- up to the full auth window for a visitor who never signs in -- so the tracker
	 * starts denied and is restarted if and when consent arrives, rather than anything being let
	 * through in the meantime.
	 */
	( loyalty || onetrust ).subscribe( () => apply( true ) );
}

if ( typeof window !== 'undefined' ) {
	bootstrap();
}
